import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { usePagination } from '../../hooks/usePagination';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { GenericView, SearchInput } from '../../components/common/table';
import { useAuth } from '../../contexts/AuthProvider';

const JobTittle = () => {
  const navigate = useNavigate();
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canJobTitle = (key) => can(key, 'jobTitle');

    // Pagination
    const pagination  = usePagination(10);

    // Search
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const handleSearchInputChange = (value) => {
        setSearch(value);
        setSearchTerm(value);
    };

    const handleSearchSubmit = () => {
        setAppliedSearchTerm(searchTerm);
        pagination.setPage(0);
    };

    // Selection
    const {
        selectedIds,
        setSelectedIds,
        handleCheckboxClick,
        handleSelectAll
    } = useSelection();

    // Data and form state
    const [jobTitles, setJobTitles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [formData, setFormData] = useState({ name: '', departmentId: '' });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');

    // Fetch job titles (match working copy)
    const fetchJobTitles = async (searchValue = appliedSearchTerm) => {
        try {
            setIsLoading(true);
            const response = await api.get(`/md/job-title-page`, {
                params: {
                    page: pagination.page,
                    size: pagination.size,
                    search: searchValue
                }
            });
            setJobTitles(response.data.content);
            pagination.setTotalPages(response.data.totalPages);
            pagination.setCurrentPageItems(response.data.content.length);
            pagination.setTotal(response.data.totalElements);
        } catch (error) {
            let errorMessage = 'Failed to fetch job titles.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view job titles.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while fetching job titles.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching job titles.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
            setJobTitles([]);
        } finally {
            setIsLoading(false);
        }
    };
    const fetchDepartments = async () => {
        try {
            const response = await api.get('/md/departement');
            setDepartments(response.data);
        } catch (error) {
            setDepartments([]);
        }
    };

    useEffect(() => {
        fetchJobTitles(appliedSearchTerm);
        fetchDepartments();
    }, [pagination.page, pagination.size, appliedSearchTerm]);

    // Open modal for create
    const handleCreate = () => {
    if (!canJobTitle('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        setFormData({ name: '', departmentId: '' });
        setEditingId(null);
        setErrors({});
        // Use the correct modal id and check for bootstrap.Modal existence
        const modalEl = document.getElementById('modal-job-title');
        if (window.bootstrap && window.bootstrap.Modal && modalEl) {
            const modal = new window.bootstrap.Modal(modalEl);
            modal.show();
        }
    }

    const handleEdit = (item) => {
    if (!canJobTitle('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
        setFormData({
            name: item.name,
            departmentId: item.departementId ? String(item.departementId) : ''
        });
        setEditingId(item.id);
        setErrors({});
        setTimeout(() => {
            const modalEl = document.getElementById('modal-job-title');
            if (window.bootstrap && window.bootstrap.Modal && modalEl) {
                const modal = new window.bootstrap.Modal(modalEl);
                modal.show();
            }
        }, 100);
    };

    // Real-time validation
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        let error = null;
        if (name === 'name') {
            error = validateField('name', value, { required: true });
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    // TomSelect change handler
    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        let error = null;
        if (name === 'departmentId') {
            error = value.length === 0 ? 'At least one Department is required' : null;
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    // Unified handleSubmit for both create and edit
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Validate name and departmentId fields only
        let error = validateField('name', formData.name, { required: true });
        let departmentError = validateField('departmentId', formData.departmentId, { required: true });
        setErrors({ name: error, departmentId: departmentError });
        if (error || departmentError) return;
        try {
            if (editingId) {
                await api.put(`/md/job-title/${editingId}`, { id: editingId, name: formData.name, departement: { id: Number(formData.departmentId) } });
                setSuccessMessage('Job Title has been successfully updated.');
            } else {
                await api.post('/md/job-title', { name: formData.name, departement: { id: Number(formData.departmentId) } });
                setSuccessMessage('Job Title has been successfully added.');
            }
            setEditingId(null);
            setFormData({ name: '', departmentId: '' });
            setErrors({});
            await fetchJobTitles();
            const modalEl = document.getElementById('modal-job-title');
            const bootstrapModal = window.bootstrap?.Modal.getInstance(modalEl);
            if (bootstrapModal) {
                bootstrapModal.hide();
                setTimeout(() => setShowSuccessModal(true), 300);
            } else {
                setShowSuccessModal(true);
            }
        } catch (error) {
            let errorMessage = 'Failed to save job title.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 400:
                        errorMessage = 'Invalid job title data provided.';
                        technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to perform this action.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 409:
                        errorMessage = 'A job title with this name already exists.';
                        technicalDetails = `Duplicate entry for name: "${formData.name}"`;
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while saving.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while saving the job title.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    // Edit selected (only works for single selection)
    const editSelected = () => {
        if (Array.isArray(selectedIds) && selectedIds.length !== 1) {
            setSuccessMessage('Please select exactly one Job Title to edit');
            setShowSuccessModal(true);
            return;
        }
        const selectedId = Array.isArray(selectedIds) ? selectedIds[0] : null;
        const selectedItem = Array.isArray(jobTitles) && jobTitles.find(item => item.id === selectedId);
        if (selectedItem) {
            handleEdit(selectedItem);
        }
    };

    // Delete selected (bulk)
    const deleteSelected = () => {
    if (!canJobTitle('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (selectedIds.length === 0) {
            setFailMessage('Please select at least one job title to delete.\n\nTechnical Details: No items selected for deletion');
            setShowFailModal(true);
            return;
        }
        setConfirmAction(() => async () => {
            try {
                await api.delete('/md/job-title/bulk', { data: selectedIds });
                setSelectedIds([]);
                await fetchJobTitles();
                setShowDeleteConfirmation(false);
                setSuccessMessage('Selected job titles have been successfully deleted.');
                setShowSuccessModal(true);
            } catch (error) {
                let errorMessage = 'Failed to delete job titles.';
                let technicalDetails = '';

                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            errorMessage = 'You do not have permission to delete these items.';
                            technicalDetails = 'Required role or permission is missing';
                            break;
                        case 404:
                            errorMessage = 'One or more job titles not found.';
                            technicalDetails = 'Some items may have been already deleted';
                            break;
                        case 500:
                            errorMessage = 'Server error while deleting items.';
                            technicalDetails = error.response.data.message || 'Internal server error during deletion';
                            break;
                        default:
                            errorMessage = 'An error occurred while deleting the items.';
                            technicalDetails = `Server responded with status: ${error.response.status}`;
                    }
                } else if (error.request) {
                    errorMessage = 'Unable to connect to the server.';
                    technicalDetails = 'Network error or server is not responding';
                } else {
                    errorMessage = 'An unexpected error occurred during deletion.';
                    technicalDetails = error.message;
                }

                setShowDeleteConfirmation(false);
                setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
                setShowFailModal(true);
            }
        });
        setShowDeleteConfirmation(true);
    };

    // Modal cleanup
    useEffect(() => {
        const modal = document.getElementById('modal-job-title');
        modal?.addEventListener('hidden.bs.modal', () => {
            setFormData({ name: '', departmentId: '' });
            setEditingId(null);
        });
        return () => {
            modal?.removeEventListener('hidden.bs.modal', () => { });
        };
    }, []);
    return (
        <>
                {/* Page header */}
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col">
                                <h2 className="page-title">
                                    Job Title
                                </h2>
                            </div>
                            {/* Page title actions */}
                            <div className="col-auto ms-auto d-print-none">
                                <div className="btn-list">
                                    <a href="#" className="btn btn-primary d-none d-sm-inline-block"
                                        onClick={handleCreate}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                        Create
                                    </a>
                                    <a href="#" className="btn btn-primary d-sm-none btn-icon" data-bs-toggle="modal"
                                        onClick={handleCreate}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Page body */}
                <div className="page-body">
                    <div className="container-xl">
                        <div className="col-12">
                            <div className="card">
                              <div className="card-body">
                                <GenericView
                                  data={jobTitles}
                                  columns={[
                                    { key: 'name', label: 'Job Title', sortable: true },
                                    { key: 'departementName', label: 'Department', sortable: true, render: (value, row) => row.departementName || (departments.find(dep => dep.id === row.departementId)?.name || '-') },
                                    { key: 'cuser', label: 'Created By', sortable: true, render: (value, row) => row.cuser?.username || 'Unknown' },
                                    { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleString() : '-' },
                                  ]}
                                  isLoading={isLoading || capabilitiesLoading}
                                  emptyStateMessage="No job titles found"
                                  checkboxSelection={true}
                                  selectedRows={selectedIds}
                                  onSelectRow={(itemId, isSelected) => {
                                    if (isSelected) {
                                      setSelectedIds([...selectedIds, itemId]);
                                    } else {
                                      setSelectedIds(selectedIds.filter(id => id !== itemId));
                                    }
                                  }}
                                  onSelectAllRows={(itemIds) => {
                                    if (Array.isArray(itemIds) && itemIds.length > 0) {
                                      setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                    } else {
                                      setSelectedIds([]);
                                    }
                                  }}
                                  customRowActions={canJobTitle('edit') ? (item) => (
                                    <a
                                      href="#"
                                      title="Detail / Hak Akses"
                                      onClick={(e) => { e.preventDefault(); navigate(`/masterdata-jobtitle/${item.id}`); }}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-list-check"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M3.5 5.5l1.5 1.5l2.5 -2.5" /><path d="M3.5 11.5l1.5 1.5l2.5 -2.5" /><path d="M3.5 17.5l1.5 1.5l2.5 -2.5" /><path d="M11 6l9 0" /><path d="M11 12l9 0" /><path d="M11 18l9 0" /></svg>
                                    </a>
                                  ) : undefined}
                                  onEdit={canJobTitle('edit') ? (item) => {
                                    setSelectedIds([item.id]);
                                    handleEdit(item);
                                  } : undefined}
                                  onDelete={canJobTitle('delete') ? (item) => {
                                    setSelectedIds([item.id]);
                                    deleteSelected();
                                  } : undefined}
                                  searchComponent={
                                    <div className="d-flex gap-2">
                                      <SearchInput
                                        value={searchTerm}
                                        onChange={handleSearchInputChange}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSearchSubmit();
                                          }
                                        }}
                                        placeholder="Search..."
                                        uniqueId="job-title-search"
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSearchSubmit}
                                      >
                                        Search
                                      </button>
                                    </div>
                                  }
                                  actionButtons={
                                    <div className="d-flex gap-2">
                                      {(canJobTitle('edit') || canJobTitle('delete')) && (
                                        <div ref={actionsContainerRef} className="dropdown">
                                          <a
                                            ref={actionsToggleRef}
                                            href="#"
                                            className="btn dropdown-toggle"
                                            data-bs-toggle="dropdown"
                                            aria-expanded={isActionsOpen}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              const inst = actionsDropdownInstanceRef.current;
                                              if (inst?.toggle) inst.toggle();
                                              else setIsActionsOpen(prev => !prev);
                                            }}
                                          >Actions</a>
                                          <div className="dropdown-menu">
                                            {canJobTitle('edit') && (
                                              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                                                style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                                Edit
                                              </a>
                                            )}
                                            {canJobTitle('delete') && (
                                              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                                style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                                Delete
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  }
                                  pagination={{
                                    currentPage: pagination.page + 1,
                                    totalRows: pagination.total,
                                    rowsPerPage: pagination.size,
                                    onPageChange: (page) => pagination.setPage(page - 1),
                                    onRowsPerPageChange: (size) => {
                                      pagination.setSize(size);
                                      pagination.setPage(0);
                                    }
                                  }}
                                  pageSizeOptions={[10, 25, 50, 100]}
                                  uniqueId="job-title-table"
                                />
                              </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Create and edit Job Title Modal */}
                <div className="modal modal-blur fade" id="modal-job-title" tabIndex="-1" role="dialog" aria-hidden="true">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editingId ? 'Edit Job Title' : 'Create Job Title'}</h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-lg-12">
                                            <div className="mb-3">
                                                <TomSelect
                                                    key={editingId || 'create'}
                                                    name="departmentId"
                                                    value={formData.departmentId ? String(formData.departmentId) : ''}
                                                    onChange={(name, value) => {
                                                        handleSelectChange(name, value);
                                                    }}
                                                    options={departments.filter(s => s && s.id && s.name).map(department => ({ id: String(department.id), name: department.name }))}
                                                    placeholder="Select Department"
                                                    label="Department"
                                                    required={true}
                                                    error={errors.departmentId}
                                                    valueField="id"
                                                    labelField="name"
                                                    searchField="name"
                                                    multiple={false}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-12">
                                            <div className="mb-3">
                                                <label className="form-label required">Job Title</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter job title"
                                                />
                                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <a href="#" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">Cancel</a>
                                    <button type="submit" className="btn btn-primary ms-auto" id="createBtn">
                                        {editingId ? 'Update' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            
            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmAction || (() => { })}
                title="Delete Confirmation"
                message={Array.isArray(selectedIds) && selectedIds.length > 0
                    ? "Are you sure you want to delete the selected Job Titles? This action cannot be undone."
                    : "Are you sure you want to delete this Job Title? This action cannot be undone."}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Success"
                message={successMessage}
                buttonText="OK"
            />
            {/* Fail Modal */}
            <FailModal
                isOpen={showFailModal}
                onClose={() => setShowFailModal(false)}
                title="Operation Failed"
                message={failMessage}
                buttonText="Close"
            />
        </>
    );
};

export default JobTittle;